import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { UpperDaysOfWeek, daysOfTheWeek, weekDays } from 'shared/Dates';
import { DatePreset, PostJobLocationState, ShiftTimes, TimesMode } from '../formTypes';
import { OngoingJobResponse } from '../postJobApi';

const DEFAULT_TIMES: ShiftTimes = { start: '08:00:00', end: '17:00:00', slots: 1 };

function defaultPerDayTimes() {
    return Object.fromEntries(daysOfTheWeek.map((day) => [day, { ...DEFAULT_TIMES }])) as Record<
        UpperDaysOfWeek,
        ShiftTimes
    >;
}

export function isCompleteDate(date: string) {
    const parsed = moment(date, 'YYYY-MM-DD', true);
    return parsed.isValid() && parsed.year() >= 1000;
}

function daysInRange(startDate: string, endDate: string): UpperDaysOfWeek[] {
    if (!startDate || !endDate) return Array.from(daysOfTheWeek);

    const start = moment(startDate);
    const end = moment(endDate);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return Array.from(daysOfTheWeek);

    const days: UpperDaysOfWeek[] = [];
    const current = start.clone();
    while (current.isSameOrBefore(end) && days.length < 7) {
        const day = current.format('dddd') as UpperDaysOfWeek;
        if (!days.includes(day)) days.push(day);
        current.add(1, 'day');
    }
    return daysOfTheWeek.filter((day) => days.includes(day));
}

function upcomingFriday() {
    const today = moment();
    return today.day() === 6 ? today.add(1, 'week').day(5) : today.day(5);
}

export function useScheduleFields(presets: PostJobLocationState | undefined) {
    const [startDate, setStartDate] = useState(presets?.startDate ?? '');
    const [endDate, setEndDate] = useState(presets?.endDate ?? '');
    const [selectedDays, setSelectedDays] = useState<UpperDaysOfWeek[]>(presets?.days ?? weekDays);
    const [timesMode, setTimesMode] = useState<TimesMode>('same');
    const [times, setTimes] = useState<ShiftTimes>(() =>
        presets?.times ? { ...DEFAULT_TIMES, ...presets.times } : { ...DEFAULT_TIMES },
    );
    const [perDayTimes, setPerDayTimes] = useState(defaultPerDayTimes);
    const [includeBreak, setIncludeBreak] = useState(false);
    const [breakLength, setBreakLength] = useState('60');

    const availableDays = useMemo(() => daysInRange(startDate, endDate), [startDate, endDate]);

    useEffect(() => {
        setSelectedDays((days) => {
            const pruned = days.filter((day) => availableDays.includes(day));
            if (pruned.length === 0 && availableDays.length < 7) return availableDays;
            return pruned.length === days.length ? days : pruned;
        });
    }, [availableDays]);

    const handleStartDateChange = (value: string) => {
        setStartDate(value);
        if (value && endDate && moment(endDate).isBefore(value)) setEndDate(value);
    };

    const applyPreset = (preset: DatePreset) => {
        const today = moment().format('YYYY-MM-DD');
        if (preset === 'singleDay') {
            const day = startDate || today;
            setStartDate(day);
            setEndDate(day);
        } else if (preset === 'thisWeek') {
            setStartDate(today);
            setEndDate(upcomingFriday().format('YYYY-MM-DD'));
        } else {
            setStartDate(today);
            setEndDate(moment().add(13, 'days').format('YYYY-MM-DD'));
        }
    };

    const activePreset: DatePreset | null = useMemo(() => {
        if (!startDate || !endDate) return null;
        if (startDate === endDate) return 'singleDay';

        const today = moment().format('YYYY-MM-DD');
        if (startDate !== today) return null;
        if (endDate === upcomingFriday().format('YYYY-MM-DD')) return 'thisWeek';
        if (endDate === moment().add(13, 'days').format('YYYY-MM-DD')) return 'nextTwoWeeks';
        return null;
    }, [startDate, endDate]);

    const setDayTimes = (day: UpperDaysOfWeek, dayTimes: ShiftTimes) => {
        setPerDayTimes((current) => ({ ...current, [day]: dayTimes }));
    };

    const applyJob = (job: OngoingJobResponse, includeDates: boolean) => {
        if (includeDates) {
            setStartDate(job.start_date ?? '');
            setEndDate(job.end_date ?? '');
        }
        setIncludeBreak(!!job.break_required);
        if (job.break_length) setBreakLength(String(job.break_length));

        const jobDayTimes: Partial<Record<UpperDaysOfWeek, ShiftTimes>> = {};
        (job.times_of_day ?? []).forEach((entry) => {
            Object.entries(entry).forEach(([day, dayTimes]) => {
                jobDayTimes[day as UpperDaysOfWeek] = {
                    start: dayTimes.start,
                    end: dayTimes.end,
                    slots: dayTimes.slots ?? 1,
                };
            });
        });
        const allowedDays = includeDates ? daysInRange(job.start_date ?? '', job.end_date ?? '') : availableDays;
        const jobDays = daysOfTheWeek.filter(
            (day) => (job.days_of_week?.includes(day) || jobDayTimes[day] !== undefined) && allowedDays.includes(day),
        );
        if (jobDays.length === 0) return;

        setSelectedDays(jobDays);
        setPerDayTimes((current) => ({ ...current, ...jobDayTimes }));

        const dayEntries = jobDays.map((day) => jobDayTimes[day]).filter(Boolean) as ShiftTimes[];
        const first = dayEntries[0];
        if (!first) return;
        const uniform = dayEntries.every(
            (entry) => entry.start === first.start && entry.end === first.end && entry.slots === first.slots,
        );
        if (uniform) {
            setTimesMode('same');
            setTimes(first);
        } else {
            setTimesMode('perDay');
        }
    };

    const snapshot = useMemo(
        () => ({ startDate, endDate, selectedDays, timesMode, times, perDayTimes, includeBreak, breakLength }),
        [startDate, endDate, selectedDays, timesMode, times, perDayTimes, includeBreak, breakLength],
    );

    return {
        startDate,
        setStartDate: handleStartDateChange,
        endDate,
        setEndDate,
        applyPreset,
        activePreset,
        availableDays,
        selectedDays,
        setSelectedDays,
        timesMode,
        setTimesMode,
        times,
        setTimes,
        perDayTimes,
        setDayTimes,
        includeBreak,
        setIncludeBreak,
        breakLength,
        setBreakLength,
        applyJob,
        snapshot,
    };
}
